import React from 'react'

import { Box, Button, Text, TogglerCheckbox } from '@factorialco/deprecated-design-system'
import {
  ContactFragment,
  FinanceValueObjectsVendorPreferredPaymentMethodEnum,
  UpdatePaymentDetailsFinanceVendorDocument,
} from 'generated/resources/graphql'

import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

interface VendorFormValues {
  id: string
  bankCode?: string
  iban?: string
  preferredPaymentMethod?: FinanceValueObjectsVendorPreferredPaymentMethodEnum | undefined
}

interface Props {
  vendor: ContactFragment
  onSuccess?: (bankCode?: string, iban?: string) => void
  editable?: boolean
}

const BankInfoForm: React.FC<Props> = ({ vendor, onSuccess, editable = true }) => {
  const [submitting, setSubmitting] = React.useState<boolean>(false)
  const { company } = useCurrentSession()

  const [updatePaymentDetailsFinanceVendor] = useMutation(
    UpdatePaymentDetailsFinanceVendorDocument,
    {
      extractorFn: (data) => data.financeMutations.updatePaymentDetailsFinanceContact,
    }
  )

  const { values, errors, setErrors, setValues, handleSubmit, isValid, dirty, touched } =
    useForm<VendorFormValues>({
      enableReinitialize: true,

      defaultValues: {
        id: vendor.id,
        bankCode: vendor.bankCode ?? undefined,
        iban: vendor.iban ?? undefined,
        preferredPaymentMethod: vendor.preferredPaymentMethod ?? undefined,
      },

      onSubmit: async (values) => {
        setSubmitting(true)

        const variables = {
          companyId: company.id,
          bankCode: values.bankCode,
          iban: values.iban,
          preferredPaymentMethod: values.preferredPaymentMethod,
        }

        const result = await updatePaymentDetailsFinanceVendor({
          variables: { ...variables, id: values.id },
        })
        const handled = handleResult(result, (errors) => {
          if (errors) {
            setErrors(errors)
            app.setFlash({
              type: 'wrong',
              message: i18n.t('finance.vendors.vendor_detail.messages.vendor_update_error'),
            })
          }
        })

        setSubmitting(false)

        if (handled.success) {
          app.setFlash({
            type: 'correct',
            message: i18n.t('finance.vendors.vendor_detail.messages.vendor_updated'),
          })

          onSuccess?.(values.bankCode, values.iban)
        }
        return handled
      },
    })

  const changePreferredPaymentMethod = (value: boolean) => {
    setValues({
      ...values,
      preferredPaymentMethod: value ? 'banktransfer' : 'card',
    })
  }

  return (
    <>
      <TextField
        label={i18n.t('finance.vendors.vendor_form.fields.iban')}
        value={values.iban ?? undefined}
        onChange={(value) => setValues({ ...values, iban: value })}
        error={touched.iban ? errors.iban : undefined}
        readonly={!editable}
      />
      <TextField
        label={i18n.t('finance.vendors.vendor_form.fields.bank_code')}
        value={values.bankCode ?? undefined}
        onChange={(value) => setValues({ ...values, bankCode: value })}
        error={touched.bankCode ? errors.bankCode : undefined}
        readonly={!editable}
      />
      <Box paddingTop="s12" flexDirection="row" gap="s8" alignItems="center">
        <TogglerCheckbox
          value={values.preferredPaymentMethod == 'banktransfer'}
          onChange={changePreferredPaymentMethod}
          disabled={!editable}
        />
        <Text size="300">
          {i18n.t('finance.vendors.vendor_form.fields.preferred_payment_method')}
        </Text>
      </Box>
      {(!vendor || dirty) && (
        <Box alignItems="flexEnd">
          <Button onClick={() => handleSubmit()} disabled={!editable || !isValid || submitting}>
            {i18n.t('common.submit')}
          </Button>
        </Box>
      )}
    </>
  )
}

export default BankInfoForm
